/**
 * MultiActivityReviewStep — Step 3 of the log-activity multi-add flow.
 *
 * By the time this renders, the Entry step has persisted everything via
 * `POST /imports` and handed us the resulting BulkImport ids. We fetch
 * activities filtered by those ids and render the existing `ActivityTable`
 * with real CO₂e from the calculation pipeline.
 *
 * "Cancel" undoes every BulkImport from this submission and returns to
 * Step 2 with the user's staged rows still intact (they live in the parent
 * `ActivityFormFlow` state).
 *
 * "Continue" advances to Step 4 (evidence). Activities exist in the DB
 * either way; Continue is just navigation.
 */

import { useCallback, useState } from 'react';
import { Button as ReactstrapButton } from 'reactstrap';
import { Loader2 } from 'lucide-react';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import {
  useGetActivitiesQuery,
  useUndoImportMutation,
  useDeleteActivityMutation,
  useTriggerCalculationMutation,
} from '../../../api/emissions';
import { ActivityTable } from '../../ActivityTable';
import { CalculationDetailsDrawer } from '../../CalculationDetailsDrawer';
import type { ActivityRecord } from '../../../types/emissions';

const PAGE_SIZE = 100;

export interface MultiActivityReviewStepProps {
  initiativeId: string;
  /** BulkImport ids returned by the Entry step's submit. */
  importIds: string[];
  /** User cancels — we undo every import in `importIds` then call this. */
  onCancel: () => void;
  /** User confirms — advances to evidence step. */
  onContinue: () => void;
}

export const MultiActivityReviewStep = ({
  initiativeId,
  importIds,
  onCancel,
  onContinue,
}: MultiActivityReviewStepProps) => {
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityRecord | null>(null);
  const [recalculatingActivityId, setRecalculatingActivityId] = useState<string | null>(null);

  const [undoImport] = useUndoImportMutation();
  const [deleteActivity] = useDeleteActivityMutation();
  const [triggerCalculation, { isLoading: isRecalculating }] = useTriggerCalculationMutation();

  const handleDelete = useCallback(
    async (activityId: string) => {
      const res = await deleteActivity({ initiativeId, activityId });
      if ('error' in res) {
        generateToast({ color: 'danger', message: 'Could not delete this activity. Please try again.' });
        return;
      }
      generateToast({ color: 'success', message: 'Activity deleted.' });
    },
    [deleteActivity, initiativeId],
  );

  const handleRecalculate = useCallback(
    async (activityId: string, activityInitiativeId: string) => {
      setRecalculatingActivityId(activityId);
      try {
        await triggerCalculation({
          initiativeId: activityInitiativeId,
          activityId,
          options: { recalculate: true },
        }).unwrap();
        generateToast({ color: 'success', message: 'Emissions recalculated successfully' });
      } catch (error) {
        toastFromError({ error, title: 'Calculation failed' });
      } finally {
        setRecalculatingActivityId(null);
      }
    },
    [triggerCalculation],
  );

  const handleViewCalculation = useCallback((activity: ActivityRecord) => {
    setSelectedActivity(activity);
  }, []);

  const handleCloseCalculationDrawer = useCallback(() => {
    setSelectedActivity(null);
  }, []);

  // Server-backed: fetch the just-created activities so they render with
  // real CO₂e from the calculation pipeline.
  const { data, isFetching, refetch } = useGetActivitiesQuery(
    {
      initiativeId,
      bulkImportIds: importIds.join(','),
      limit: PAGE_SIZE,
      offset: 0,
    },
    { skip: importIds.length === 0 },
  );

  const activities = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  const handleCancelConfirm = useCallback(async () => {
    setIsCancelling(true);
    try {
      // Undo every import in this submission. Run sequentially so a partial
      // failure leaves the rest reversible — we report success / partial.
      const failed: string[] = [];
      for (const id of importIds) {
        const res = await undoImport({ initiativeId, importId: id });
        if ('error' in res) {
          failed.push(id);
        }
      }
      if (failed.length > 0) {
        generateToast({
          color: 'warning',
          message: `Undid ${importIds.length - failed.length} of ${importIds.length} imports — please try again for the rest.`,
        });
      }
      onCancel();
    } finally {
      setIsCancelling(false);
      setConfirmCancelOpen(false);
    }
  }, [importIds, undoImport, initiativeId, onCancel]);

  return (
    <>
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-6'>
          <div className='tw:flex tw:items-start tw:justify-between tw:mb-4 tw:gap-4'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1'>
                Review submitted activities ({total})
              </h5>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                These activities are now saved with their calculated emissions. Use the row menu to view a calculation, swap factors, or delete an individual record. Click Cancel to remove the whole batch.
              </p>
            </div>
            <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? (
                <>
                  <Loader2 className='tw:size-4 tw:animate-spin' />
                  Refreshing…
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>

          <ActivityTable
            activities={activities}
            isLoading={isFetching}
            onDelete={handleDelete}
            onRecalculate={(activityId) => {
              const activity = activities.find((a) => a._id === activityId);
              handleRecalculate(activityId, activity?.initiativeId ?? initiativeId);
            }}
            onViewCalculation={handleViewCalculation}
            recalculatingActivityId={recalculatingActivityId}
            columns={{ asset: false, added: false, factor: true }}
          />

          <div className='tw:flex tw:justify-between tw:items-center tw:mt-4'>
            <Button variant='outline' onClick={() => setConfirmCancelOpen(true)} disabled={isCancelling}>
              Cancel batch
            </Button>
            <Button
              onClick={onContinue}
              className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
            >
              Continue to Evidence
            </Button>
          </div>
        </CardContent>
      </Card>

      <CalculationDetailsDrawer
        isOpen={selectedActivity !== null}
        toggle={handleCloseCalculationDrawer}
        initiativeId={selectedActivity?.initiativeId ?? initiativeId}
        activityId={selectedActivity?._id ?? null}
        onRecalculate={handleRecalculate}
        isRecalculating={isRecalculating}
        activityData={selectedActivity ? {
          activityType: selectedActivity.activityType,
          bulkImportId: selectedActivity.bulkImportId,
          quantity: selectedActivity.quantity,
          unit: selectedActivity.unit,
          region: selectedActivity.region,
          periodStart: selectedActivity.periodStart,
          periodEnd: selectedActivity.periodEnd,
          scope: selectedActivity.scope,
          fuelType: selectedActivity.fuelType,
          vehicleType: selectedActivity.vehicleType,
          refrigerantType: selectedActivity.refrigerantType,
          processType: selectedActivity.processType,
          regionFallback: selectedActivity.regionFallback,
          yearFallback: selectedActivity.yearFallback,
          calculationNotices: selectedActivity.calculationNotices,
        } : undefined}
      />

      {confirmCancelOpen && (
        <ConfirmationModal
          isOpen={true}
          title='Cancel this submission?'
          content={
            <p className='tw:text-sm tw:mb-0'>
              This will remove all {total} activities from this submission and return you to the Add Activities step.
              Your staged rows will still be there so you can edit and resubmit.
            </p>
          }
          confirmButton={
            <ReactstrapButton color='danger' onClick={handleCancelConfirm} disabled={isCancelling}>
              {isCancelling ? 'Cancelling…' : 'Cancel batch'}
            </ReactstrapButton>
          }
          handleCancel={() => setConfirmCancelOpen(false)}
        />
      )}
    </>
  );
};
